import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { api } from "@/lib/api";
import { PRICE, PLAN_LOOKUP_KEY } from "@/lib/pricing";
import { toast } from "sonner";
import { MockVoiceBars } from "@/components/MockVoiceBars";
import {
  Video, Scale, Upload, Check, Play, ArrowRight, Clapperboard,
  Volume2, Maximize, ThumbsUp, Bell, MessageSquare, AudioLines,
  Eye, Search, ShieldCheck, Sparkles, UserRound, Move, Type, MessageSquarePlus,
} from "lucide-react";

const SKATE = "/skate-demo.jpg";
const IMG = (base, w = 900) => `${base.split("?")[0]}?auto=format&fit=crop&w=${w}&q=70`;

// Source videos being reacted to.
const SOURCES = {
  skate: SKATE,
  concert: IMG("https://images.unsplash.com/photo-1459749411175-04bf5292ceea", 900),
  gaming: IMG("https://images.unsplash.com/photo-1598550480917-1c485268676e", 900),
};

// The actual product layout: the source video fills the frame, the creator's
// voice bars sit in a fixed box top-right with a blinking reactionvideo.app label.
function StudioMock({ source = SOURCES.skate, hero = false, label = null, seed = 0 }) {
  return (
    <div className={`relative overflow-hidden aspect-video w-full bg-zinc-900 ${hero ? "rounded-2xl border border-rose-900/60 shadow-[0_0_60px_rgba(225,29,72,0.15)]" : "rounded-xl border border-zinc-300 group-hover:border-rose-400 transition-colors"}`}>
      {/* source video */}
      <img src={source} alt="source video" className="absolute inset-0 w-full h-full object-cover" loading={hero ? "eager" : "lazy"} />
      <div className="absolute inset-0 bg-black/10" />

      {/* playhead / scrubber */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent pt-[9%] pb-[3.5%] px-[4%]">
        <div className="relative h-[3px] bg-white/25 rounded-full mb-[2.5%]">
          <div className="absolute left-0 top-0 h-full w-2/5 bg-rose-500 rounded-full" />
          <div className="absolute left-2/5 top-1/2 -translate-y-1/2 w-[7px] h-[7px] rounded-full bg-rose-500" />
        </div>
        {hero && (
          <div className="flex items-center justify-between text-white/90">
            <div className="flex items-center gap-2.5">
              <Play size={12} fill="currentColor" />
              <Volume2 size={12} />
              <span className="text-[9px] font-mono text-white/70">1:48 / 4:20</span>
            </div>
            <Maximize size={11} className="text-white/70" />
          </div>
        )}
      </div>

      {/* REC badge */}
      <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-black/70 rounded-full px-2.5 py-1 text-[10px] font-mono text-white">
        <span className="w-2 h-2 rounded-full bg-rose-500 rec-pulse" /> REC
      </div>
      {label && (
        <div className="absolute top-12 left-3 text-[12px] font-semibold text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]">{label}</div>
      )}

      {/* voice bars + blinking reactionvideo.app label, fixed top-right, animated like live speech */}
      <MockVoiceBars seed={seed} />
    </div>
  );
}

const STEPS = [
  { n: "01", t: "Pick a video", d: "Search a topic or browse what's trending, then hit \"React to this.\" The source title, channel and thumbnail load into your studio automatically, credited and ready before you touch a single setting." },
  { n: "02", t: "Add your comments", d: "Pick a voice and drag the voice bars where you want them. Hit Enter Comment wherever you want to react, type your line, tap Improve with AI if you like, and save it. No camera, no microphone, no face." },
  { n: "03", t: "Publish to YouTube", d: "Press Publish Video, check the title, description, hashtags and visibility, then hit Publish to YouTube. The video plays, pauses at each comment while the AI voice speaks, records in your browser and uploads straight to your channel as a standard 16:9 video. We never keep a copy." },
];

const FEATURES = [
  { icon: UserRound, t: "Stay anonymous", d: "Instead of your face, glowing red, grey and white voice bars with a blinking reactionvideo.app light sit on the video wherever you drag them and move as the AI voice speaks. No camera or microphone is ever used." },
  { icon: Move, t: "Bars your way", d: "Drag the voice-bars box anywhere on the video so it never covers the important part. It starts in the top-right corner and remembers where you put it." },
  { icon: MessageSquare, t: "Timed comments", d: "Every saved comment lands in the Comments list in time order. Jump back to its moment, edit it, play it in your voice, or delete it anytime." },
  { icon: Sparkles, t: "AI makes every comment punchier", d: "Tap Improve with AI in the comment popup for a clearer, snappier line, or Re-polish any saved comment for a fresh version. You can always change the wording by hand." },
  { icon: AudioLines, t: "One AI voice per video", d: "Choose from eight voices and press play to hear each one. Preview any comment in that voice before you create the video — no text appears on screen." },
  { icon: Video, t: "Pause-and-speak playback", d: "Hit Publish to YouTube and the source plays from the beginning, pauses at each comment while the AI voice speaks, then carries on — recorded as one standard 16:9 video right in your browser." },
  { icon: MessageSquarePlus, t: "Enter Comment, anywhere", d: "Hit Enter Comment and the video pauses right there. Type your reaction in the popup, tap Improve with AI if you like, then save it to that exact moment." },
  { icon: Type, t: "Title & description, your way", d: "Edit the video title and description under the video or in the Publish popup, with live character counters that keep you inside YouTube's limits." },
  { icon: Eye, t: "Hashtags & visibility control", d: "Save your default hashtags, pick public, unlisted or private, and edit the title and description before anything goes live." },
  { icon: Upload, t: "Publish without leaving the app", d: "One click records the finished video and uploads it to your connected YouTube channel with your title, description and hashtags, then gives you the live link." },
  { icon: ShieldCheck, t: "Your video stays yours", d: "The video is made in your browser and passed straight through to YouTube. We never store or keep a copy, and AI runs on your own OpenAI key." },
  { icon: Scale, t: "The original creator, always credited", d: "The source title and link are pre-filled at the top of every description so credit is never an afterthought." },
];

const PLAN = [
  "Unlimited reactions — record & publish as many as you want",
  "Animated voice bars stand in for you — no camera or mic needed",
  "Timed comments, polished by AI and read by an AI voice",
  "Everything recorded locally in your browser",
  "Standard 16:9 YouTube videos, every time",
  "Enter Comment popup with Improve with AI",
  "Custom hashtags, visibility & editable metadata",
  "Publish straight to your own YouTube channel",
  "AI runs on your own OpenAI key — no video stored",
];

const FAQ = [
  { q: "Where does AI come in?", a: "Only in two places: it makes each comment you type punchier, and an AI voice reads your comments aloud. Both run on your own OpenAI key, saved in Settings and billed to you by OpenAI." },
  { q: "How much does it cost?", a: "One flat monthly fee for the whole studio — record and publish unlimited reactions. No per-video charges, no usage meters, no surprise overages. Cancel anytime and your recordings stay yours." },
  { q: "Where is my video made and stored?", a: "Entirely in your browser, on your own machine. The source video, its audio and the AI voice are recorded together locally and the finished file goes straight from your browser to YouTube when you publish. We never host or keep a copy." },
  { q: "Do I need a camera or a microphone?", a: "No. You never appear on screen or record your voice. Animated voice bars stand in for you, you type your comments, and an AI voice reads them." },
  { q: "Why desktop only?", a: "Capturing a browser tab with its audio only works in desktop Chrome and Edge. Mobile browsers can't record the source video, so we'd rather say so up front than ship a version that fails at the Publish button." },
  { q: "Does it download the source video?", a: "No. The source plays in a real tab and is captured as screen content, the same way any screen recorder works. Nothing is ripped or stored from YouTube, and the original creator is credited in every description." },
  { q: "Can I edit things before publishing?", a: "Yes. You confirm the title, description, hashtags and visibility in the Publish popup before anything records or uploads. Nothing publishes until you say so, and you can cancel while it records." },
  { q: "Will my reaction get claimed or struck?", a: "That call belongs to YouTube and the rights holder, not to us, and no tool can promise otherwise. Keep your commentary present, add something to the clip, and the credit line lives in every description we pre-fill." },
];

export default function Landing() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const live = !!user; // buttons active only once logged in; public sees them inactive
  const go = () => { if (user) navigate("/dashboard"); };

  const showcase = useMemo(() => ([
    { source: SOURCES.concert, label: "Reacting to a live set" },
    { source: SOURCES.gaming, label: "Reacting to a gaming clip" },
  ]), []);

  const purchase = async () => {
    if (!live) return;
    try {
      const res = await api.post("/payments/checkout", {
        lookup_key: PLAN_LOOKUP_KEY,
        origin_url: window.location.origin,
        user_id: user?.user_id || null,
      });
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error("Could not start checkout");
    }
  };

  return (
    <div className="bg-white text-zinc-900 min-h-screen">
      {/* NAV */}
      <header className="fixed top-0 w-full z-50">
        <div className="bg-zinc-950/90 backdrop-blur-md border-b border-zinc-800 text-white">
          <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
            <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-3 font-display font-bold text-3xl text-white" data-testid="landing-logo">
              <Clapperboard size={34} className="text-rose-500" />
              Reaction <span className="text-orange-500 -ml-1">Video</span>
            </Link>
            <div className="flex items-center gap-5">
              <Link to="/legal" className="text-sm text-zinc-400 hover:text-white transition-colors" data-testid="nav-legal">Legal</Link>
              <span className="text-zinc-600 select-none" aria-hidden="true">|</span>
              {user ? (
                <button onClick={() => navigate("/dashboard")} data-testid="nav-dashboard" className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-[4px] px-5 py-2 transition-colors">Dashboard</button>
              ) : (
                <button onClick={login} data-testid="nav-login" className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-[4px] px-5 py-2 transition-colors">Log in</button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* HERO */}
      <div className="bg-zinc-950 text-white border-b border-zinc-800">
        <section className="pt-40 pb-16 px-6 max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-1 text-[11px] uppercase tracking-widest text-zinc-300 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-500 rec-pulse" /> 100% anonymous · no camera
            </div>
            <h1 className="font-display font-extrabold tracking-tight text-5xl lg:text-6xl leading-[1.05]">
              React without your face.<br />
              <span className="text-orange-500">Ship the whole video.</span>
            </h1>
            <p className="text-zinc-400 mt-6 text-base leading-relaxed max-w-lg">
              Pick a YouTube video, type your comments at the moments that matter, and an AI voice speaks them while the video pauses. It's recorded in your browser and published straight to your channel. No camera, no microphone, no footage stored.
            </p>
            <div className="flex items-center gap-4 mt-8">
              <button onClick={go} disabled={!live} data-testid="hero-cta" className="bg-red-600 hover:bg-red-700 disabled:cursor-not-allowed shadow-[0_0_25px_rgba(220,38,38,0.35)] text-white font-semibold rounded-[4px] px-10 py-4 text-lg transition-colors flex items-center gap-2.5">
                Get Reaction Video <ArrowRight size={20} />
              </button>
            </div>
          </div>
          <div className="fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="rounded-[4px]" style={{ boxShadow: "0 0 28px rgba(255,197,40,0.45), 0 4px 16px rgba(255,197,40,0.30)" }} data-testid="hero-image-glow">
              <StudioMock hero />
            </div>
            <p className="text-xs text-white mt-6 font-mono text-center" data-testid="hero-caption">Publish To Your Channel</p>
          </div>
        </section>
      </div>

      {/* HOW IT WORKS */}
      <section id="how" className="py-16 px-6 max-w-7xl mx-auto">
        <div className="eyebrow mb-3">How it works</div>
        <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-12 text-zinc-900">Three steps from a video you love to a reaction on your channel</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {STEPS.map((s) => (
            <div key={s.n} className="bg-zinc-50 border border-zinc-200 shadow-sm rounded-[4px] p-8 hover:border-zinc-300 hover:shadow-md transition-all" data-testid={`step-${s.n}`}>
              <div className="text-orange-500 font-mono text-sm mb-4">{s.n}</div>
              <h3 className="font-display font-semibold text-lg mb-3 text-zinc-900">{s.t}</h3>
              <p className="text-zinc-600 text-sm leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* STUDIO SHOWCASE */}
      <section className="py-16 px-6 max-w-7xl mx-auto">
        <div className="eyebrow mb-3">Inside the studio</div>
        <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-4 text-zinc-900">Your voice bars, reacting to whatever you're watching</h2>
        <p className="text-zinc-600 text-sm leading-relaxed mb-12 w-full">
          Glowing voice bars with a blinking reactionvideo.app light sit on the video wherever you drag them and move with the voice. At each comment the source pauses, the AI voice speaks your polished line, then the video carries on — all in a single 16:9 take.
        </p>
        <div className="grid md:grid-cols-2 gap-6">
          {showcase.map((h, idx) => (
            <div key={h.label} data-testid={`showcase-${h.label}`}>
              <StudioMock source={h.source} hero label={h.label} seed={idx + 1} />
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-16 px-6 max-w-7xl mx-auto">
        <div className="eyebrow mb-3">Everything inside</div>
        <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-4 text-zinc-900">One tab, from the first clip to the published video</h2>
        <p className="text-zinc-600 text-sm mb-12">Reaction Video isn't a screen recorder with extras bolted on — finding a video, writing your comments, creating the reaction and publishing it all live in one place.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f) => (
            <div key={f.t} className="bg-zinc-50 border border-zinc-200 shadow-sm rounded-[4px] p-7 hover:border-zinc-300 hover:shadow-md transition-all">
              <f.icon size={18} className="text-orange-500 mb-4" />
              <h3 className="font-display font-semibold text-[15px] mb-2 text-zinc-900">{f.t}</h3>
              <p className="text-zinc-600 text-[13px] leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section className="py-16 px-6 max-w-6xl mx-auto text-center">
        <div className="eyebrow mb-3">Pricing</div>
        <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight text-zinc-900">One plan. Everything in it.</h2>
        <p className="text-zinc-500 text-sm mt-3 mb-12">One flat monthly fee, unlimited reactions. No per-video charges from us. AI comments and voice are billed to your own OpenAI key.</p>
        <div className="border border-zinc-800 rounded-[4px] p-10 bg-zinc-950 text-white shadow-sm text-left" data-testid="pricing-card">
          <h3 className="font-display font-bold text-3xl md:text-4xl text-center text-white">Creator Plan: One Channel</h3>
          <p className="text-zinc-400 text-sm text-center mt-2">The whole reaction studio, one flat price, unlimited videos.</p>
          <div className="text-center my-8">
            <span className="font-display font-extrabold text-6xl text-white">{PRICE}</span>
            <span className="text-zinc-400 text-sm ml-2">per month</span>
            <div className="text-[11px] text-zinc-500 mt-2">Cancel anytime. Your recordings stay yours.</div>
          </div>
          <div className="grid md:grid-cols-2 gap-x-10 gap-y-3 mb-10">
            {PLAN.map((p) => (
              <div key={p} className="flex items-start gap-2.5 text-sm text-zinc-300">
                <Check size={15} className="text-emerald-500 mt-0.5 shrink-0" /> {p}
              </div>
            ))}
          </div>
          <button onClick={purchase} disabled={!live} data-testid="purchase-btn" className="w-full bg-red-600 hover:bg-red-700 disabled:cursor-not-allowed text-white font-medium rounded-[4px] py-3.5 transition-colors">
            Purchase Now
          </button>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-6 max-w-6xl mx-auto">
        <div className="eyebrow mb-3">Questions</div>
        <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-12 text-zinc-900">Before you sign up</h2>
        <div className="grid md:grid-cols-2 gap-5">
          {FAQ.map((f) => (
            <div key={f.q} className="bg-zinc-50 border border-zinc-200 shadow-sm rounded-[4px] p-7">
              <h3 className="font-display font-semibold mb-2 text-[15px] text-zinc-900">{f.q}</h3>
              <p className="text-zinc-600 text-[13px] leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="bg-zinc-950 text-zinc-400 border-t border-zinc-800 py-10 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-xs">
          <div className="flex items-center gap-3 font-display font-bold text-2xl text-white">
            <Clapperboard size={28} className="text-rose-500" /> Reaction <span className="text-orange-500 -ml-1">Video</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/legal" className="hover:text-white transition-colors" data-testid="footer-legal">Legal</Link>
            <span className="text-zinc-600 select-none" aria-hidden="true">|</span>
            {user ? (
              <button onClick={() => navigate("/dashboard")} data-testid="footer-login" className="text-zinc-400 hover:text-white transition-colors">Dashboard</button>
            ) : (
              <button onClick={login} data-testid="footer-login" className="text-zinc-400 hover:text-white transition-colors">Log in</button>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}
